import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Service, Appointment, BlockedTime, Category, SalonSettings, DEFAULT_SALON_SETTINGS } from '../types';
import { useAuth } from './AuthContext';
import { categoriesApi, salonSettingsApi } from '../firebase/services';

interface AppContextType {
  services: Service[];
  appointments: Appointment[];
  blockedTimes: BlockedTime[];
  categories: Category[];
  salonSettings: SalonSettings;
  loading: {
    services: boolean;
    appointments: boolean;
    blockedTimes: boolean;
  };
  refreshServices: () => Promise<void>;
  refreshAppointments: () => Promise<void>;
  refreshBlockedTimes: () => Promise<void>;
  refreshCategories: () => Promise<void>;
  refreshSalonSettings: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userData } = useAuth();
  
  const [services, setServices] = useState<Service[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [salonSettings, setSalonSettings] = useState<SalonSettings>(DEFAULT_SALON_SETTINGS);

  const [loading, setLoading] = useState({
    services: true,
    appointments: true,
    blockedTimes: true,
  });

  // Fetch services on initial load
  const refreshServices = useCallback(async () => {
    setLoading(prev => ({ ...prev, services: true }));
    try {
      const servicesCollection = collection(db, 'services');
      const servicesSnapshot = await getDocs(servicesCollection);
      const servicesList = servicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Service[];
      
      setServices(servicesList);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(prev => ({ ...prev, services: false }));
    }
  }, []);

  // Fetch appointments based on user role
  const refreshAppointments = useCallback(async () => {
    console.log('[AppContext] Starting refreshAppointments...');
    setLoading(prev => ({ ...prev, appointments: true }));
    try {
      // Calculer la date d'il y a 7 jours et dans 60 jours pour optimiser
      const today = new Date();
      const pastDate = new Date(today);
      pastDate.setDate(pastDate.getDate() - 7);
      const futureDate = new Date(today);
      futureDate.setDate(futureDate.getDate() + 60);
      
      const pastDateStr = pastDate.toISOString().split('T')[0];
      const futureDateStr = futureDate.toISOString().split('T')[0];
      
      let appointmentsQuery;
      
      if (currentUser && userData && userData.role === 'admin') {
        // Admin voit les rendez-vous des 60 prochains jours
        appointmentsQuery = query(
          collection(db, 'appointments'),
          where('date', '>=', pastDateStr),
          where('date', '<=', futureDateStr)
        );
      } else if (currentUser) {
        // Clients connectés voient uniquement leurs rendez-vous
        appointmentsQuery = query(
          collection(db, 'appointments'),
          where('clientId', '==', currentUser.uid)
        );
      } else {
        // Visiteurs non connectés voient les RDV des 60 prochains jours (pour les disponibilités)
        appointmentsQuery = query(
          collection(db, 'appointments'),
          where('date', '>=', pastDateStr),
          where('date', '<=', futureDateStr)
        );
      }
      
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      const appointmentsList = appointmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Appointment[];
      
      console.log(`[AppContext] Loaded ${appointmentsList.length} appointments`);
      setAppointments(appointmentsList);
    } catch (error) {
      console.error('[AppContext] Error fetching appointments:', error);
    } finally {
      console.log('[AppContext] Finished refreshAppointments');
      setLoading(prev => ({ ...prev, appointments: false }));
    }
  }, [currentUser, userData]);

  // Fetch categories
  const refreshCategories = useCallback(async () => {
    try {
      const cats = await categoriesApi.getAll();
      setCategories(cats);
    } catch (error) {
      console.error('[AppContext] Error fetching categories:', error);
    }
  }, []);

  // Fetch salon settings
  const refreshSalonSettings = useCallback(async () => {
    try {
      const settings = await salonSettingsApi.get();
      setSalonSettings(settings);
    } catch (error) {
      console.error('[AppContext] Error fetching salon settings:', error);
    }
  }, []);

  // Fetch blocked times
  const refreshBlockedTimes = useCallback(async () => {
    console.log('[AppContext] Starting refreshBlockedTimes...');
    setLoading(prev => ({ ...prev, blockedTimes: true }));
    try {
      const blockedTimesCollection = collection(db, 'blockedTimes');
      const blockedTimesSnapshot = await getDocs(blockedTimesCollection);
      const blockedTimesList = blockedTimesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BlockedTime[];
      
      console.log(`[AppContext] Loaded ${blockedTimesList.length} blocked times`);
      setBlockedTimes(blockedTimesList);
    } catch (error) {
      console.error('[AppContext] Error fetching blocked times:', error);
    } finally {
      console.log('[AppContext] Finished refreshBlockedTimes');
      setLoading(prev => ({ ...prev, blockedTimes: false }));
    }
  }, []);

  // Initial data loading
  useEffect(() => {
    refreshServices();
    refreshAppointments();
    refreshBlockedTimes();
    refreshCategories();
    refreshSalonSettings();
  }, [refreshAppointments, refreshBlockedTimes, refreshServices, refreshCategories, refreshSalonSettings]);

  const value = {
    services,
    appointments,
    blockedTimes,
    categories,
    salonSettings,
    loading,
    refreshServices,
    refreshAppointments,
    refreshBlockedTimes,
    refreshCategories,
    refreshSalonSettings,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
